"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Map as LeafletMap } from "leaflet"
import dynamic from "next/dynamic"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"

/* ────────────────────────────────────────────
   Locations strip — compact reassurance that the tests happen in
   real Swiss cities: a map with a pin per city beside the city
   list. Made for the membership page, between the timeline (what)
   and the frequency picker (cost); the blood ad pages show it under
   the doctors (Eugene, 21.10). Each page passes its own copy.

   Visual: static Switzerland outline (echoing the locations-page
   map style) sits behind a wrap of glass-pill city names. The
   pills are intentionally non-interactive — this section answers
   "where" at a glance rather than serving as a picker. The list
   itself is sourced from the cities CMS, deduped server-side.
   ──────────────────────────────────────────── */

// Coordinates per city. Drives the pin positions on the Leaflet
// terrain map; coming-soon entries share the same table so a single
// lookup powers both states.
const CITY_LAYOUT: Record<string, { lat: number; lng: number }> = {
  // Active blood-clinic cities
  zurich: { lat: 47.376, lng: 8.541 },
  bern: { lat: 46.948, lng: 7.447 },
  basel: { lat: 47.56, lng: 7.588 },
  horgen: { lat: 47.26, lng: 8.598 },
  zug: { lat: 47.166, lng: 8.515 },
  "st-gallen": { lat: 47.424, lng: 9.377 },
  winterthur: { lat: 47.5, lng: 8.724 },
  // Coming soon
  lausanne: { lat: 46.52, lng: 6.633 },
  geneva: { lat: 46.204, lng: 6.143 },
  montreux: { lat: 46.434, lng: 6.911 },
  schaffhausen: { lat: 47.697, lng: 8.633 },
}

// The Leaflet map is heavy and only needs to run client-side. Lazy
// load it so it doesn't bloat the membership page's main client
// chunk; ssr:false avoids the SSR-incompatible window/document
// access that react-leaflet's MapContainer makes at import time.
const MembershipLocationsMap = dynamic(
  () =>
    import("@/components/ui/membership-locations-map").then(
      (m) => m.MembershipLocationsMap
    ),
  { ssr: false }
)

// The /locations page's map: terrain tiles in its green tint, a pin per
// clinic, nearby clinics clustered under a count that zooms in on click, and
// each pin's popup linking to its clinic page. The blood ad pages show it
// (Eugene, 21.10); the membership page keeps the plain pins above.
const LocationMap = dynamic(
  () => import("@/components/ui/location-map").then((m) => m.LocationMap),
  {
    ssr: false,
    // The tile colour under the tint, as on /locations, so the map does not
    // pop from white to green when the tiles arrive.
    loading: () => (
      <div
        className="h-full w-full"
        style={{ background: "var(--color-sand-100)" }}
      />
    ),
  }
)

/** A clinic on the /locations-style map. `path` is the clinic page's
 *  unlocalized path, `/locations/{city}/{clinic}`. */
export type LocationsStripClinic = {
  id: string
  city: string
  clinic: string
  address: string
  type: "mri" | "blood"
  lat: number
  lng: number
  path: string
}

function ClinicsMap({
  clinics,
  backLabel,
  zoomInLabel,
  zoomOutLabel,
}: {
  clinics: LocationsStripClinic[]
  backLabel: string
  zoomInLabel: string
  zoomOutLabel: string
}) {
  const lp = useLocalePath()
  const withHrefs = useMemo(
    () => clinics.map(({ path, ...c }) => ({ ...c, href: lp(path) })),
    [clinics, lp]
  )
  // The map fits Switzerland once, when it mounts, at the size it has then.
  // A size that changes afterwards (a phone turned, a window resized, the
  // column settling while the page loads) left it on a world view (Eugene,
  // 21.10), so it mounts only once the square has a size and mounts afresh,
  // fitted again, whenever the size changes.
  const boxRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const box = boxRef.current
    if (!box) return
    // Measured at once as well: a ResizeObserver reports only with the next
    // paint, which a background tab can hold back.
    setWidth(Math.round(box.getBoundingClientRect().width))
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width))
    )
    observer.observe(box)
    return () => observer.disconnect()
  }, [])
  // A cluster zooms in on click; the button flies back to the country view.
  const mapRef = useRef<LeafletMap | null>(null)
  const [zoomedIn, setZoomedIn] = useState(false)
  const onMap = useCallback((map: LeafletMap | null) => {
    mapRef.current = map
    setZoomedIn(false)
    // The country fit lands before its zoom becomes the minimum, so check
    // again when the minimum changes (zoomlevelschange), or the button shows
    // on the untouched map.
    map?.on("zoomend zoomlevelschange", () =>
      setZoomedIn(map.getZoom() > map.getMinZoom() + 0.05)
    )
  }, [])
  return (
    // Square while the column is wide enough to hold it (Eugene, 21.10).
    // `isolate` keeps Leaflet's z-indexed panes and the back button under
    // the sticky navbar.
    <div
      ref={boxRef}
      className="locations-strip-map relative isolate aspect-square w-full overflow-hidden rounded-[20px]"
      style={{ background: "var(--color-sand-100)" }}
    >
      <style>{`.locations-strip-map .leaflet-tile-pane img { filter: saturate(0.48) !important; }`}</style>
      {width > 0 && (
        <LocationMap
          key={width}
          clinics={withHrefs}
          fullBleed
          mapPadTop={24}
          mapPad={0.08}
          tileTint={{ color: "#4fa176", opacity: 0.3 }} // ds-color-ignore: translucent overlay, as on /locations
          mapRefCallback={onMap}
        />
      )}
      {/* Zoom buttons, as on /locations (Eugene, 21.10); minus stops at the
          country view, which is the map's minimum zoom. */}
      <div className="absolute right-3 bottom-3 z-[500] flex flex-col overflow-hidden rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.12)]">
        <button
          type="button"
          onClick={() => mapRef.current?.zoomIn()}
          aria-label={zoomInLabel}
          title={zoomInLabel}
          className="flex size-10 cursor-pointer items-center justify-center border-b border-neutral-200/80 text-neutral-800 hover:bg-neutral-50"
        >
          <svg
            aria-hidden
            width="18"
            height="18"
            viewBox="0 0 22 22"
            fill="none"
          >
            <path
              d="M11 4v14M4 11h14"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => mapRef.current?.zoomOut()}
          disabled={!zoomedIn}
          aria-label={zoomOutLabel}
          title={zoomOutLabel}
          className="flex size-10 cursor-pointer items-center justify-center text-neutral-800 hover:bg-neutral-50 disabled:cursor-default disabled:text-neutral-300 disabled:hover:bg-white"
        >
          <svg
            aria-hidden
            width="18"
            height="18"
            viewBox="0 0 22 22"
            fill="none"
          >
            <path
              d="M4 11h14"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      {zoomedIn && (
        <button
          type="button"
          onClick={() => mapRef.current?.fire("returnToCountry")}
          aria-label={backLabel}
          title={backLabel}
          className="absolute top-3 right-3 z-[500] flex size-10 cursor-pointer items-center justify-center rounded-full bg-white text-neutral-800 shadow-[0_1px_4px_rgba(0,0,0,0.12)] hover:bg-neutral-50"
        >
          {/* The /locations page's back-to-country arrow. */}
          <svg
            aria-hidden
            width="20"
            height="20"
            viewBox="0 -960 960 960"
            fill="currentColor"
          >
            <path d="M280-200v-80h284q63 0 109.5-40T720-420q0-60-46.5-100T564-560H312l104 104-56 56-200-200 200-200 56 56-104 104h252q97 0 166.5 63T800-420q0 94-69.5 157T564-200H280Z" />
          </svg>
        </button>
      )}
    </div>
  )
}

// Slug order for coming-soon cities, paired positionally with the
// `locationsSoonCities` translation string. Keeping the slug list
// here lets the translation stay a simple human-edited comma list
// while still mapping each entry to its coordinate.
const SOON_SLUGS = ["montreux"]

// Total pins the map will render — mirrors the filter the map applies
// to drop entries without a `CITY_LAYOUT` coord. Surfaced on the strip
// as `data-pin-count` so the e2e test asserts against the live CMS-
// derived count instead of a hand-bumped constant.
function countMembershipPins(
  active: { slug: string }[],
  soon: { slug: string }[]
): number {
  return (
    active.filter((c) => CITY_LAYOUT[c.slug]).length +
    soon.filter((c) => CITY_LAYOUT[c.slug]).length
  )
}

// Renders the `#f4f4f4` tile + the Leaflet terrain map. Stretches
// to the parent grid cell's height (`h-full`) so it matches the
// neighbouring text column height — no more squashed aspect-locked
// silhouette.
function LocationsMap({
  active,
  soon,
}: {
  active: { slug: string; name: string }[]
  soon: { slug: string; name: string }[]
}) {
  const pins = [
    ...active.flatMap((c) => {
      const l = CITY_LAYOUT[c.slug]
      return l
        ? [
            {
              slug: c.slug,
              name: c.name,
              lat: l.lat,
              lng: l.lng,
              status: "active" as const,
            },
          ]
        : []
    }),
    ...soon.flatMap((c) => {
      const l = CITY_LAYOUT[c.slug]
      return l
        ? [
            {
              slug: c.slug,
              name: c.name,
              lat: l.lat,
              lng: l.lng,
              status: "soon" as const,
            },
          ]
        : []
    }),
  ]
  return (
    // Negative margins bleed past the card's md:p-14 padding on
    // top/bottom/left so the map sits closer to the card edges,
    // while leaving the right column at its original 56px padding.
    // 56 (card) − 32 (negative margin) = 24px gap to card edge.
    // The matching `h-[calc(100%+64px)]` adds back the 32+32 of
    // height the negative margins would otherwise eat — without it,
    // the tile only shifts up but doesn't grow, so its bottom edge
    // sits short of the right column's bottom by 32px.
    <div className="bg-background relative h-full min-h-[240px] overflow-hidden rounded-[20px] md:-mt-8 md:-mb-8 md:-ml-8 md:h-[calc(100%+64px)] md:min-h-[360px]">
      <MembershipLocationsMap pins={pins} />
    </div>
  )
}

export type LocationsStripCopy = {
  label: string
  heading: string
  description: string
  legendActive: string
  soonLabel: string
  /** Comma list of coming-soon city names, paired with `SOON_SLUGS`. */
  soonCities: string
  /** The quiet line under the list; left out when empty. */
  checkupNote?: string
  cta: string
  /** The map's back-to-country button, on the /locations-style map. */
  mapBack?: string
  /** The map's zoom buttons. */
  mapZoomIn?: string
  mapZoomOut?: string
}

export function LocationsStrip({
  cities,
  copy,
  clinics,
}: {
  cities: { slug: string; name: string }[]
  copy: LocationsStripCopy
  /** Show the /locations map with these clinics, on a white band of its own
   *  instead of the white card (the blood ad pages, Eugene 21.10). */
  clinics?: LocationsStripClinic[]
}) {
  const bare = clinics !== undefined
  const lp = useLocalePath()
  if (cities.length === 0) return null
  const soon = copy.soonCities
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name, i) => ({ slug: SOON_SLUGS[i] ?? name.toLowerCase(), name }))
  const pinCount = countMembershipPins(cities, soon)
  return (
    <section
      id="locations-strip"
      data-pin-count={pinCount}
      className={cn(
        "relative",
        // The white band runs on from the doctors' white section above,
        // whose bottom padding is the gap, so no top padding of its own.
        bare ? "bg-white pb-16 md:pb-24" : "bg-background py-3 md:py-4"
      )}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div
          className={cn(
            "relative",
            !bare &&
              "overflow-hidden rounded-[24px] bg-white px-6 py-10 md:rounded-[32px] md:px-14 md:py-14"
          )}
        >
          <div
            className={cn(
              "relative grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16",
              bare ? "items-center" : "items-stretch"
            )}
          >
            <div className="h-full">
              {clinics ? (
                <ClinicsMap
                  clinics={clinics}
                  backLabel={copy.mapBack ?? ""}
                  zoomInLabel={copy.mapZoomIn ?? ""}
                  zoomOutLabel={copy.mapZoomOut ?? ""}
                />
              ) : (
                <LocationsMap active={cities} soon={soon} />
              )}
            </div>

            <div>
              <div className="flex flex-col gap-6 text-left md:gap-7">
                <div className="flex flex-col gap-2">
                  <p className="type-label-md mb-3 text-neutral-500">
                    {copy.label}
                  </p>
                  <h2 className="type-display-md text-neutral-900">
                    {copy.heading}
                  </h2>
                  <p className="type-body-lg mt-2 max-w-[460px] text-neutral-600 md:mt-0">
                    {copy.description}
                  </p>
                </div>
                {/* Two columns: column 1 has the "Available now" header
                    plus the first 5 active cities; column 2 continues the
                    active list with the remaining cities, then drops the
                    "Coming soon" header and the soon cities. Splitting
                    this way keeps the section height roughly balanced
                    instead of having a very tall left column and a short
                    right one. */}
                {(() => {
                  // Split active cities so the right column has ~enough room
                  // for the "Coming soon" header + soon list without the
                  // section getting taller. With 8 active + 3 soon today,
                  // 6/2 keeps the columns visually even.
                  const SPLIT = 6
                  const activeCol1 = cities.slice(0, SPLIT)
                  const activeCol2 = cities.slice(SPLIT)
                  const activeItem = (c: { slug: string; name: string }) => (
                    <li
                      key={c.slug}
                      className="type-body-lg inline-flex items-center gap-2.5 text-neutral-900"
                    >
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full bg-neutral-900"
                      />
                      {c.name}
                    </li>
                  )
                  const soonItem = (c: { slug: string; name: string }) => (
                    <li
                      key={c.slug}
                      className="type-body-lg inline-flex items-center gap-2.5 text-neutral-500"
                    >
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full border-[1.5px] border-neutral-400 bg-white"
                      />
                      {c.name}
                    </li>
                  )
                  return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <div className="flex flex-col gap-3">
                        <h3 className="type-label-lg text-neutral-500 normal-case">
                          {copy.legendActive}
                        </h3>
                        <ul className="flex flex-col gap-1.5">
                          {activeCol1.map(activeItem)}
                        </ul>
                      </div>
                      <div className="flex flex-col gap-3">
                        <ul className="flex flex-col gap-1.5">
                          {activeCol2.map(activeItem)}
                        </ul>
                        {soon.length > 0 && (
                          <>
                            <h3 className="type-label-lg text-neutral-500 normal-case">
                              {copy.soonLabel}
                            </h3>
                            <ul className="flex flex-col gap-1.5">
                              {soon.map(soonItem)}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })()}
                {/* Quiet line bridging the membership-only locations
                    to the broader Ahead network — context, not a
                    competing CTA. */}
                {copy.checkupNote && (
                  <p className="type-body-md max-w-[460px] text-neutral-500">
                    {copy.checkupNote}
                  </p>
                )}
                <div>
                  <a
                    href={lp("/locations")}
                    className={cn(buttonVariants({ variant: "outline" }))}
                  >
                    {copy.cta}
                    <ArrowRightIcon className="ml-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
